import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ArrowRight } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import Portrait from "@/components/Portrait";

const slugify = (s = "") => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function LeadCard({ m }) {
  return (
    <Link
      to={`/team/${m.slug}`}
      className="group relative mx-auto block w-full max-w-md rounded-2xl border-2 border-gold/50 bg-navy-950 p-8 text-center shadow-[0_24px_60px_-20px_rgba(11,19,43,0.5)] transition-transform duration-300 hover:-translate-y-1.5"
      data-testid={`team-lead-${slugify(m.fullName)}`}
    >
      <span className="absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-gold px-4 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-navy-950">
        <Crown className="h-3 w-3" aria-hidden /> Team Lead
      </span>
      <Portrait name={m.fullName} src={m.photograph} size="lg" className="mx-auto" testid={`team-lead-photo-${slugify(m.fullName)}`} />
      <h3 className="mt-6 font-serif text-2xl text-ivory transition-colors group-hover:text-gold">{m.fullName}</h3>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.15em] text-gold">{m.designation}</p>
      {m.bio ? (
        <p className="mt-4 text-sm leading-relaxed text-slate-300 line-clamp-3">{m.bio}</p>
      ) : (
        <p className="mt-4 rounded-lg border border-dashed border-white/15 bg-navy-900/60 px-4 py-3 text-xs italic leading-relaxed text-slate-500" data-testid="profile-placeholder">
          Photograph & profile details will be added soon.
        </p>
      )}
      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-gold transition-colors group-hover:text-gold-light">
        View Full Profile <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}

function MemberCard({ m }) {
  return (
    <Link
      to={`/team/${m.slug}`}
      className="card-premium group block h-full text-center"
      data-testid={`team-member-${slugify(m.fullName)}`}
    >
      <Portrait name={m.fullName} src={m.photograph} size="md" className="mx-auto" testid={`team-member-photo-${slugify(m.fullName)}`} />
      <h3 className="mt-5 font-serif text-xl text-navy-900 transition-colors group-hover:text-gold-dark">{m.fullName}</h3>
      <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold-dark">{m.designation}</p>
      {m.bio ? (
        <p className="mt-4 text-sm leading-relaxed text-slate-600 line-clamp-3">{m.bio}</p>
      ) : (
        <p className="mt-4 rounded-lg border border-dashed border-navy-900/15 bg-ivory px-4 py-3 text-xs italic leading-relaxed text-slate-400" data-testid="profile-placeholder">
          Profile details will be added soon.
        </p>
      )}
      <span className="link-underline mt-5 justify-center text-xs">
        View Profile <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}

function Connector() {
  return (
    <div aria-hidden className="hidden sm:block">
      <div className="mx-auto h-10 w-px bg-gold/40" />
      <div className="mx-auto h-px w-3/4 bg-gold/40" />
    </div>
  );
}

export default function Team() {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setMembers(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const departments = useMemo(() => {
    const map = new Map();
    members.forEach((m) => {
      const d = m.department || "Our Team";
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(m);
    });
    return [...map.entries()];
  }, [members]);

  return (
    <div data-testid="team-page">
      <PageHero
        eyebrow="Our People"
        title="Meet The Team"
        lede="The marketers, storytellers, relationship-builders and people-champions working behind the scenes at Roy Career Solutions Private Limited — building the future of learning, together."
      />

      <section className="border-b border-navy-900/10 bg-white py-10" data-testid="team-stats">
        <div className="container-rcs flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
          <span className="text-sm text-slate-600" data-testid="team-count">
            <span className="font-serif text-2xl text-navy-900">{members.length}</span> Team Members
          </span>
          <span className="hidden h-6 w-px bg-navy-900/15 sm:block" aria-hidden />
          <span className="text-sm text-slate-600" data-testid="team-dept-count">
            <span className="font-serif text-2xl text-navy-900">{departments.length}</span> Departments
          </span>
          <span className="hidden h-6 w-px bg-navy-900/15 sm:block" aria-hidden />
          <span className="text-sm text-slate-600">
            <span className="font-serif text-2xl text-navy-900">2</span> Education Brands Supported
          </span>
        </div>
      </section>

      {departments.map(([dept, list], di) => {
        const lead = list.find((m) => m.isLead);
        const rest = list.filter((m) => m !== lead);
        return (
          <section
            key={dept}
            className={`py-20 lg:py-28 ${di % 2 === 1 ? "border-y border-navy-900/10 bg-white" : ""}`}
            data-testid={`team-dept-${slugify(dept)}`}
          >
            <div className="container-rcs">
              <Section
                eyebrow={`Department ${String(di + 1).padStart(2, "0")}`}
                title={dept}
                className="mx-auto text-center [&>p]:mx-auto"
              />

              {lead && (
                <Reveal delay={0.1} className="mt-14">
                  <LeadCard m={lead} />
                </Reveal>
              )}

              {lead && rest.length > 0 && <Connector />}
              {!lead && <div className="mt-4" />}

              {rest.length > 0 && (
                <div className="mt-2 flex flex-wrap justify-center gap-8">
                  {rest.map((m, i) => (
                    <Reveal key={m.id} delay={0.15 + i * 0.08} className="w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]">
                      <div className="flex h-full flex-col">
                        {(lead || di > 0) && <div aria-hidden className="mx-auto mb-5 hidden h-6 w-px bg-gold/40 sm:block" />}
                        <MemberCard m={m} />
                      </div>
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })}

      <section className="bg-navy-950 py-20" data-testid="team-join">
        <div className="container-rcs text-center">
          <Section
            dark
            eyebrow="Join Us"
            title="Want to see your name here?"
            lede="We are always interested in hearing from educators, technologists, marketers and builders who share our vision for education."
            className="mx-auto [&>p]:mx-auto"
          />
          <Reveal delay={0.15}>
            <a href="/careers" className="btn-gold mt-10" data-testid="team-careers-cta">
              Explore Careers at RCS
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
